import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, LabelList } from 'recharts';
import Metrics, { Type, Formatter, execute, formatRatio } from './Metrics'
import Player from '../models/Player';
import Api from '../Api';

const api = new Api();

const Leaderboard: React.FC = () => {
    const [players, setPlayers] = useState<Player[]>([]);
    const [{ format }, setFormatterState] = useState<{ format: Formatter }>({ format: formatRatio })
    const [lockText, setLockText] = useState<string>('')
    const [rankIsLocked, setRankIsLocked] = useState<boolean>(false)

    const setFormatter = (format: Formatter): void => {
        setFormatterState({ format })
    }

    const apiFetch = () => {
        (async () => {
            const players = await api.get()
            execute({ players, setFormatter, setPlayers, setLockText, rankIsLocked }, Type.POINTS_GAMES)
        })()
    }

    useEffect(apiFetch, []);

    const rankLockIcon = rankIsLocked
        ? '🔒 Unlock'
        : '🔓 Lock'

    return (
        <>
            <h1>Leaderboard</h1>
            <div className="controls">
                <button id="axis-locker" onClick={() => {setRankIsLocked(!rankIsLocked)}}>{rankLockIcon} Rank {lockText}</button>
                <Metrics
                    setFormatter={setFormatter}
                    setPlayers={setPlayers}
                    players={players}
                    setLockText={setLockText}
                    rankIsLocked={rankIsLocked}
                />
            </div>
            <ResponsiveContainer width="90%" height={players.length * 42}>
                <BarChart data={players} layout="vertical">
                    <XAxis type="number" dataKey="metric" orientation="top" tick={true} axisLine={true} />
                    <YAxis yAxisId={1} type="category" dataKey="username" tickLine={false} axisLine={false} />
                    <YAxis yAxisId={0} type="category" dataKey="rank" width={25} axisLine={false} />
                    <Bar dataKey="metric" fill="#8884d8" className="bar">
                        <LabelList dataKey="metric" formatter={format} />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
            <section>
                <h2>How do I read this?</h2>
                <h3>Games</h3>
                <p>
                    This is how many games you've played.
                    Gluttons simply win. No discrimination towards imbeciles or geniuses.
                </p>
                <h3>Wins</h3>
                <p>
                    This is how many games you've won.
                    <span aria-label="beers" role="img">🍻</span>
                </p>
                <h3>Wins/Games</h3>
                <p>
                    This is the percentage of games you've won.
                    This metric kinda sucks because some games only have 3 people while others may have 7.
                    You are statistically more likely to win a 3-player game than a 7-player one,
                    which is not factored in at all with this metric. <span aria-label="no good" role="img">👎</span>
                </p>
                <h3 id="eligible">Eligible</h3>
                <p>
                    Every game has a point value. There is 1 point for every non-bot player.
                    This is how many points you've been eligible to win in sum from all games played.
                </p>
                <h3>Points</h3>
                <p>
                    This is how many points you've won in all. See <a href="#eligible">eligible</a>.
                </p>
                <h3>Points/Eligible</h3>
                <p>
                    This is the percentage of points you've won out of all the eligible points you've played in games.
                    This metric is weighted, so it's slightly better than the opaque wins/games, but it still favors winners of small games.
                    <span aria-label="confused" role="img">😕</span>
                </p>
                <h3>Points/Games</h3>
                <p>
                    This is the best metric because it factors game size over a standard average,
                    so if you more frequently win larger games, then that's accounted in your favor here.
                    1.0 is the mathematical average.
                    If you're above 1.0, then you win more than you lose.
                </p>
                <h3>Eligible/Games</h3>
                <p>
                    Average game size. This tells you about how many people a player usually plays with.
                </p>
                <h2>Show me the Data!</h2>
                <p><a target="_blank" rel="noopener noreferrer" href="https://github.com/jpuck/tien-len-db/blob/main/users.json">users.json</a></p>
                <h2>How are points calculated?</h2>
                <p>
                    After a game, each player submits a claim with the player IDs of the losers and the winner.
                    If there is consensus among the claims, then the eligible points are incremented for each player and the winner's kill points are awarded.
                </p>
                <p><a target="_blank" rel="noopener noreferrer" href="https://github.com/jpuck/tien-len-db/blob/main/scripts/conduct-census.js">source</a></p>
                <h2>Why am I on here twice?</h2>
                <p>
                    If you're using legacy login, then you probably changed your password or username.
                    I would suggest using Discord login, or a password manager between devices.
                    You can merge your accounts by submitting a pull request with an alias <a target="_blank" rel="noopener noreferrer" href="https://github.com/jpuck/tien-len-db/blob/main/aliases.json">here</a>.
                </p>
                <h2>How do I get on the board?</h2>
                <p>
                    You must win a game and have played a minimum of 10 games with at least one other real (non-bot) player.
                </p>
            </section>
        </>
    );
}

export default Leaderboard
